import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  BOOKING_DATE_TIME_UPDATE_FORM_FIELDS,
  dropdownAtomFamily,
  Log,
  type IDropdownItem,
  type IDropdownItemShifts,
} from "@ryde/shared";

import {
  resolveDirectionFromShift,
  resolveShiftTimeForDirection,
} from "../helpers";

/**
 * The shift field of the bulk date/time panel is a shortcut, not a value that is saved: picking one
 * fills the direction, and the time then follows whichever direction is applied. The date is
 * deliberately left alone.
 */
export const useBulkBookingDateTimeForm = () => {
  const { setValue, watch } = useFormContext();
  const shiftDropdownItems: IDropdownItemShifts[] = useRecoilValue(
    dropdownAtomFamily("shift"),
  );

  const shiftId = watch(BOOKING_DATE_TIME_UPDATE_FORM_FIELDS.shiftId);
  const direction = watch(BOOKING_DATE_TIME_UPDATE_FORM_FIELDS.direction);
  const selectedShift = shiftDropdownItems.find(
    ({ value }) => value === shiftId,
  );

  const onShiftSelect = useEvent((shift?: IDropdownItem) => {
    Log.debug("onShiftSelect event of the date and time panel, shift:", shift);

    if (!shift) {
      return;
    }

    // Only the direction is set here; the effect below derives the time from it, so the shift's
    // start/end choice is made in one place rather than once here and again on every direction change.
    setValue(
      BOOKING_DATE_TIME_UPDATE_FORM_FIELDS.direction,
      resolveDirectionFromShift(shift as IDropdownItemShifts),
      { shouldDirty: true, shouldValidate: true },
    );
  });

  useEffect(() => {
    const shiftTime = resolveShiftTimeForDirection({
      shift: selectedShift,
      direction,
    });

    // With no shift chosen the time is the operator's own, so it is never overwritten here.
    if (!shiftTime) {
      return;
    }

    setValue(BOOKING_DATE_TIME_UPDATE_FORM_FIELDS.time, shiftTime, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [direction, selectedShift, setValue]);

  return {
    onShiftSelect,
  };
};
